import { t } from "i18next";
import type { IconType } from "../../icon";
import type { ExamStatus } from "../../types";
import type { ButtonProps } from "../Button";
import type { TasksSectionItem } from "./types";

export const getStatRows = <T>(stats: T[]): T[][] =>
	stats.length === 4 ? [stats.slice(0, 2), stats.slice(2)] : [stats];

const TWO_COLUMN_MIN_WIDTH = 720;
const DESKTOP_CARDS_TO_SHOW = 2.15;

export const computeTaskCardWidth = (
	availableWidth: number,
	gap: number,
	totalCards: number,
): number => {
	// Desktop peeks at a quarter of the next card when there are enough to
	// scroll; with one or two tasks, size as two columns so a lone card never
	// spans the full row.
	if (availableWidth >= TWO_COLUMN_MIN_WIDTH) {
		const cardsToShow =
			totalCards > 2 ? DESKTOP_CARDS_TO_SHOW : Math.max(totalCards, 2);
		return (availableWidth - gap * (cardsToShow - 1)) / cardsToShow;
	}
	const cardsToShow = Math.min(availableWidth < 480 ? 1.1 : 1.5, totalCards);
	return (availableWidth - gap * (cardsToShow - 1)) / cardsToShow;
};

export const STACK_FOOTER_MAX_CARD_WIDTH = 380;
export const COMPACT_CHIPS_MAX_CARD_WIDTH = 480;

type RecommendedCardLayoutParams = {
	cardWidth?: number;
	isMobile: boolean;
	isSmallScreen: boolean;
};

type RecommendedCardLayout = {
	stackFooter: boolean;
	useCompactChips: boolean;
};

/**
 * Derives the card footer layout from the card's own measured width rather
 * than the device viewport, so a card stays consistent regardless of how
 * many columns share the screen. Falls back to viewport breakpoints when the
 * card hasn't been measured yet (e.g. before the carousel's first layout
 * pass, or callers that render the card at its natural width).
 */
export const getRecommendedCardLayout = ({
	cardWidth,
	isMobile,
	isSmallScreen,
}: RecommendedCardLayoutParams): RecommendedCardLayout => {
	if (cardWidth === undefined) {
		return { stackFooter: isSmallScreen, useCompactChips: isMobile };
	}
	return {
		stackFooter: cardWidth < STACK_FOOTER_MAX_CARD_WIDTH,
		useCompactChips: cardWidth < COMPACT_CHIPS_MAX_CARD_WIDTH,
	};
};

export const getButtonIconAndLabel = (
	status: ExamStatus,
	kind?: TasksSectionItem["kind"],
): { icon?: IconType; label: string } | null => {
	if (kind === "session" && status !== "COMPLETE") {
		return {
			icon: "play-circle-outline",
			label: t("dashboard.recommendedTasks.startSessionAction"),
		};
	}

	switch (status) {
		case "ACTIVE":
			return {
				icon: "play-pause",
				label: t("dashboard.recommendedTasks.resumeAction"),
			};
		case "COMPLETE":
			return {
				icon: "chart-box-outline",
				label: t("dashboard.recommendedTasks.reviewResultsAction"),
			};
		case "NOT_STARTED":
			return {
				icon: "play-circle-outline",
				label: t("dashboard.recommendedTasks.startTestAction"),
			};
		default:
			return null;
	}
};

/**
 * Mobile always uses `text` so the CTA never competes with the "Quick Study"
 * FAB. Desktop has no FAB, so emphasis follows status.
 */
export const getRecommendedCtaMode = (
	status: ExamStatus,
	isMobile: boolean,
): NonNullable<ButtonProps["mode"]> => {
	if (isMobile) {
		return "text";
	}
	switch (status) {
		case "NOT_STARTED":
			return "contained";
		case "ACTIVE":
			return "outlined";
		default:
			return "text";
	}
};
